'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { Cross2Icon } from '@radix-ui/react-icons'
import { cn } from '../utils'
import type { ComponentProps } from 'react'

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogContentWrapper = DialogPrimitive.Content

type DialogOverlayProps = ComponentProps<typeof DialogPrimitive.Overlay>

const DialogOverlay = ({ className, ...props }: DialogOverlayProps) => (
	<DialogPrimitive.Overlay
		className={cn(
			'fixed desktop:rounded-[10px] inset-0 z-50 bg-[#f6f6f3]/60 dark:bg-[#0C0C0C]/80 data-[state=closed]:animate-[dialog-overlay-hide_100ms] data-[state=open]:animate-[dialog-overlay-show_100ms]',
			className,
		)}
		{...props}
	/>
)

type DialogContentProps = ComponentProps<typeof DialogContentWrapper> & {
	hideClose?: boolean
}

const DialogContent = ({ className, children, hideClose, ...props }: DialogContentProps) => (
	<DialogPortal>
		<DialogOverlay />
		<DialogContentWrapper
			className={cn(
				'bg-background content border-border border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[calc(100svh-10vw)] overflow-y-scroll w-[90vw] max-w-xl dark:p-px text-primary z-50 data-[state=closed]:animate-[dialog-content-hide_100ms] data-[state=open]:animate-[dialog-content-show_100ms]',
				className,
			)}
			{...props}
		>
			{children}

			{!hideClose && (
				<DialogPrimitive.Close className='absolute right-6 top-6 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground'>
					<Cross2Icon className='h-4 w-4' />
					<span className='sr-only'>Close</span>
				</DialogPrimitive.Close>
			)}
		</DialogContentWrapper>
	</DialogPortal>
)

type DialogContentFramelessProps = ComponentProps<typeof DialogContentWrapper>

const DialogContentFrameless = ({ className, children, ...props }: DialogContentFramelessProps) => (
	<DialogPortal>
		<DialogOverlay />
		<DialogContentWrapper
			className={cn(
				'fixed bg-background frameless-content top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-xl border dark:border-none dark:p-px text-primary z-50 data-[state=closed]:animate-[dialog-content-hide_100ms] data-[state=open]:animate-[dialog-content-show_100ms]',
				className,
			)}
			{...props}
		>
			{children}
		</DialogContentWrapper>
	</DialogPortal>
)

const DialogHeader = ({ className, ...props }: ComponentProps<'div'>) => (
	<div className={cn('flex flex-col space-y-1.5 text-left', className)} {...props} />
)

const DialogFooter = ({ className, ...props }: ComponentProps<'div'>) => (
	<div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
)

type DialogTitleProps = ComponentProps<typeof DialogPrimitive.Title>

const DialogTitle = ({ className, ...props }: DialogTitleProps) => (
	<DialogPrimitive.Title
		className={cn('text-lg font-semibold leading-none tracking-tight mb-4', className)}
		{...props}
	/>
)

type DialogDescriptionProps = ComponentProps<typeof DialogPrimitive.Description>

const DialogDescription = ({ className, ...props }: DialogDescriptionProps) => (
	<DialogPrimitive.Description className={cn('text-sm text-878787', className)} {...props} />
)

export {
	Dialog,
	DialogContent,
	DialogContentWrapper,
	DialogContentFrameless,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
}
